import '.a{display:flex;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.ci{margin-top:96px;}.ssr-plugin'
import '.ew{margin-bottom:96px;}.ssr-plugin'
import '.cj{max-width:872px;}.ssr-plugin'
import '.ck{flex-grow:1;}.ssr-plugin'
import '.ag{justify-content:space-between;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.ac{flex-direction:column;}.ssr-plugin'
import '.ah{gap:16px;}.ssr-plugin'
import '.cn{flex-direction:row;}.ssr-plugin'
import '.l{gap:12px;}.ssr-plugin'
import '.cm{margin-bottom:16px;}.ssr-plugin'
import { useEffect, useState } from 'react'
import { useClient, useQuery } from '@based/sdk/react'
import { hash } from '@saulx/hash'
import { prettyDate } from '@based/pretty-date'
import { ActionList, ActionListItem } from '../ActionList/index.js'
import { Avatar } from '../Avatar/index.js'
import { Button } from '../Button/index.js'
import { Dialog } from '../Dialog/index.js'
import { InlineFileInput } from '../InlineFileInput/index.js'
import { Loader } from '../Loader/index.js'
import { Modal } from '../Modal/index.js'
import { ScrollArea } from '../ScrollArea/index.js'
import { Stack } from '../Stack/index.js'
import { Text } from '../Text/index.js'
import { TextInput } from '../TextInput/index.js'
import { registerPasskey } from '../Passkeys/registerPasskey.js'
import { styled } from 'inlines'
import { useToast } from '../Toast/index.js'
import { userAvatarColors } from '../UserMenu/index.js'

type UserObject = {
  name: string
  email: string
  sessions: {
    id: number
    date: number
    device: 'desktop' | 'tablet' | 'mobile' | 'unknown'
    os: string
    browser: string
    country: string
    current?: boolean
  }[]
}
export const AccountSettings = ({
  userObject,
}: {
  userObject?: UserObject
}) => {
  const client = useClient()
  const toast = useToast()
  const { data, loading } = useQuery(
    userObject ? null : 'ui-account-settings',
    {
      token: client.authState?.token,
    },
  )
  const user = userObject || data
  const [newName, setNewName] = useState<string>('')

  useEffect(() => {
    setNewName(user?.name || '')
  }, [user?.name])

  return !user || loading ? (
    <Loader />
  ) : (
    <ScrollArea>
      <div className="a c f ci ew"
      >
        <div className="cj a ck ag d"
        >
          <div className="a ac f ah"
          >
            <Text variant="heading-medium" color="neutral100">
              Account settings
            </Text>
            <Text variant="display-regular" color="neutral60">
              Manage your account and devices
            </Text>
            <Text
              variant="subheading-medium"
              color="neutral100"
              style={{
                marginTop: 96,
                marginBottom: 16,
              }}
            >
              Account details
            </Text>
            <ActionList
              items={[
                <ActionListItem
                  key="displayName"
                  title="Display name"
                  description="Nickname or first name, or how you’d like to be called."
                  action={
                    <Modal title="Edit display name" size="small">
                      <Modal.Trigger>
                        <Button variant="border" trailIcon="edit">
                          {user.name}
                        </Button>
                      </Modal.Trigger>
                      <Modal.Content>
                        <TextInput
                          value={newName}
                          onChange={setNewName}
                          type="text"
                          label="Display name"
                          placeholder="Add your name"
                        />
                      </Modal.Content>
                      <Modal.Footer>
                        <Button
                          keyHint="Esc"
                          variant="border"
                          align="center"
                          onClick={() => {
                            Dialog.Close()
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          keyHint="Enter"
                          align="center"
                          onClick={async () => {
                            await client.call('ui-account-settings-update', {
                              name: newName,
                            })
                            Dialog.Close()
                          }}
                        >
                          Save
                        </Button>
                      </Modal.Footer>
                    </Modal>
                  }
                />,
                <ActionListItem
                  key="accountEmail"
                  title="Account email"
                  description="Important messages and verification links will be sent here."
                  action={
                    <Button variant="border" disabled trailIcon="edit">
                      {user.email}
                    </Button>
                  }
                />,
                <ActionListItem
                  key="profilePicture"
                  title="Profile picture"
                  description="Use a custom image as your profile picture."
                  action={
                    <div className="a cn l"
                    >
                      <Avatar
                        src={user?.picture}
                        label={user.name}
                        color={
                          userAvatarColors[
                            hash(user.email + user.name) %
                              userAvatarColors.length
                          ]
                        }
                      />
                      <InlineFileInput
                        accept="image/*"
                        multiple={false}
                        onChange={async (_e, url) => {
                          await client.call('ui-account-settings-update', {
                            picture: url,
                          })
                        }}
                      >
                        <Button variant="border">Upload new</Button>
                      </InlineFileInput>
                      <Dialog
                        title="Remove profile picture?"
                        description="Your profile picture will be removed — instead, a monogram and a randomized color will be used as your avatar."
                      >
                        <Dialog.Trigger>
                          <Button variant="border" disabled={!user.picture}>
                            Remove
                          </Button>
                        </Dialog.Trigger>
                        <Dialog.Content>
                          <Stack gap={12}>
                            <Button
                              width="full"
                              keyHint="Esc"
                              variant="border"
                              align="center"
                              onClick={() => {
                                Dialog.Close()
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              width="full"
                              keyHint="Enter"
                              align="center"
                              onClick={async () => {
                                await client.call(
                                  'ui-account-settings-update',
                                  {
                                    picture: null,
                                  },
                                )
                                Dialog.Close()
                              }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Dialog.Content>
                      </Dialog>
                    </div>
                  }
                />,
              ]}
            />
            <div className="ci cm a ag cn d"
            >
              <Text variant="subheading-medium" color="neutral100">
                Devices and sessions
              </Text>
              {user.sessions.length > 1 && (
                <styled.div>
                  <Dialog
                    title={`Log out of all ${user.sessions.length - 1} sessions?`}
                    description={`You will be logged out on all devices and all your sessions will be ended.`}
                  >
                    <Dialog.Trigger>
                      <Button variant="border" color="red">
                        Log out everywhere
                      </Button>
                    </Dialog.Trigger>
                    <Dialog.Content>
                      <Stack gap={12}>
                        <Button
                          width="full"
                          keyHint="Esc"
                          variant="border"
                          align="center"
                          onClick={() => {
                            Dialog.Close()
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          width="full"
                          keyHint="Enter"
                          align="center"
                          color="red"
                          onClick={async () => {
                            await client.call('ui-account-settings-update', {
                              logout: 'all',
                            })
                            Dialog.Close()
                          }}
                        >
                          Log out
                        </Button>
                      </Stack>
                    </Dialog.Content>
                  </Dialog>
                </styled.div>
              )}
            </div>
            <ActionList
              items={user.sessions.map((session, index) => (
                <ActionListItem
                  key={`session_${index}`}
                  icon={session.device === 'mobile' ? 'phone' : 'laptop'}
                  title={`${session.os} · ${session.browser}${session.current ? ' (Current)' : ''}`}
                  description={`${session.country} · ${prettyDate(session.date, 'date-time-human')}`}
                  action={
                    session.current ? null : (
                      <Dialog
                        title={`Log out of ${session.os} · ${session.browser}?`}
                        description={`You will be logged out from this session that started ${prettyDate(session.date, 'date-time-human')} in ${session.country}.`}
                      >
                        <Dialog.Trigger>
                          <Button variant="border">Log out</Button>
                        </Dialog.Trigger>
                        <Dialog.Content>
                          <Stack gap={12}>
                            <Button
                              width="full"
                              keyHint="Esc"
                              variant="border"
                              align="center"
                              onClick={() => {
                                Dialog.Close()
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              width="full"
                              keyHint="Enter"
                              align="center"
                              onClick={async () => {
                                const result = await client.call(
                                  'ui-account-settings-update',
                                  {
                                    logout: session.id,
                                  },
                                )
                                if (result?.ok) {
                                  toast(
                                    `Logged out from ${session.os} · ${session.browser}`,
                                    {
                                      description: `Started in ${session.country}, ${prettyDate(session.date, 'date-time-human')}.`,
                                      icon: 'log-out',
                                    },
                                  )
                                }
                                Dialog.Close()
                              }}
                            >
                              Log out
                            </Button>
                          </Stack>
                        </Dialog.Content>
                      </Dialog>
                    )
                  }
                />
              ))}
            />

            <div className="ci cm a ag cn d"
            >
              <Text variant="subheading-medium" color="neutral100">
                Passkeys
              </Text>
              <styled.div>
                <Stack gap={16}>
                  <Dialog
                    title={`Register Passkey`}
                    description={`Register Passkey for a new device`}
                  >
                    <Dialog.Trigger>
                      <Button variant="border">Register new Passkey</Button>
                    </Dialog.Trigger>
                    <Dialog.Content>
                      <Stack gap={12}>
                        <Button
                          width="full"
                          keyHint="Esc"
                          variant="border"
                          align="center"
                          onClick={() => {
                            Dialog.Close()
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          width="full"
                          keyHint="Enter"
                          align="center"
                          onClick={async () => {
                            try {
                              const res = await registerPasskey({
                                client,
                                toast,
                                Dialog,
                              })
                              if (res.ok) {
                                toast('Passkey registered', {
                                  icon: 'checkmark-filled',
                                })
                                Dialog.Close()
                              }
                            } catch (error) {
                              toast('Error registering passkey', {
                                description:
                                  error instanceof Error
                                    ? error.message
                                    : String(error),
                                icon: 'error-filled',
                                color: 'red',
                              })
                              Dialog.Close()
                              return
                            }
                          }}
                        >
                          Register
                        </Button>
                      </Stack>
                    </Dialog.Content>
                  </Dialog>
                  {user.passkeys?.length ? (
                    <Dialog
                      title={`Remove all Passkeys?`}
                      description={`All your passkeys will be removed. You need to register a new one to use it.`}
                    >
                      <Dialog.Trigger>
                        <Button variant="border" color="red">
                          Remove all Passkeys
                        </Button>
                      </Dialog.Trigger>
                      <Dialog.Content>
                        <Stack gap={12}>
                          <Button
                            width="full"
                            keyHint="Esc"
                            variant="border"
                            align="center"
                            onClick={() => {
                              Dialog.Close()
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            width="full"
                            keyHint="Enter"
                            align="center"
                            color="red"
                            onClick={async () => {
                              const result = await client.call(
                                'ui-account-settings-update',
                                {
                                  removePasskey: 'all',
                                },
                              )
                              if (result?.ok) {
                                toast(`Removed all registered Passkeys`, {
                                  icon: 'delete',
                                })
                              }
                              Dialog.Close()
                            }}
                          >
                            Remove all
                          </Button>
                        </Stack>
                      </Dialog.Content>
                    </Dialog>
                  ) : null}
                </Stack>
              </styled.div>
            </div>
            {!user.passkeys?.length ? (
              <Text color="neutral60">No registered Passkeys</Text>
            ) : (
              <ActionList
                items={user.passkeys.map((passkey, index) => (
                  <ActionListItem
                    key={`passkey_${index}`}
                    icon="card"
                    title={`${passkey.device}`}
                    description={`Created ${prettyDate(passkey.createdAt, 'date-time-human')}`}
                    action={
                      <Dialog
                        title={`Delete Passkey ${passkey.id}?`}
                        description={`Your Passkey for the device ${passkey.aaguid} will be permanently deleted.`}
                      >
                        <Dialog.Trigger>
                          <Button variant="border">Delete</Button>
                        </Dialog.Trigger>
                        <Dialog.Content>
                          <Stack gap={12}>
                            <Button
                              width="full"
                              keyHint="Esc"
                              variant="border"
                              align="center"
                              onClick={() => {
                                Dialog.Close()
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              width="full"
                              keyHint="Enter"
                              align="center"
                              onClick={async () => {
                                const result = await client.call(
                                  'ui-account-settings-update',
                                  {
                                    removePasskey: passkey.id,
                                  },
                                )
                                if (result?.ok) {
                                  toast(`Removed Passkey`, {
                                    icon: 'delete',
                                  })
                                }
                                Dialog.Close()
                              }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Dialog.Content>
                      </Dialog>
                    }
                  />
                ))}
              />
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  )
}
